import { ActionError, AttentionBlock, Button, titleCase, toast } from '@westhetics/ui'
import { useState, type ReactNode } from 'react'
import { errorMessage, fieldErrors } from '@/lib/errors'
import {
  providerDown,
  useCancelAnamnesis,
  useResendAnamnesis,
  type AnamnesisChannel,
  type AnamnesisSummary,
} from './api'
import { CancelAnamnesisDialog, type CancelTarget } from './cancel-dialog'
import { CHANNEL_IN_SENTENCE, copyText } from './labels'
import { LINK_COPIED, LinkPanel } from './link-panel'

/**
 * Reenviar e cancelar, do jeito que a lista global, a aba da ficha e o registro da anamnese usam
 * (RN-ANA-03 a 05). Reenviar reemite o link da mesma anamnese e nada mais; cancelar pede
 * confirmação com o efeito escrito. O que falha fica na tela, com a saída: quando o provedor do
 * canal está fora, o link novo já existe e pode ser copiado ou ir por outro canal.
 */

export type ActionTarget = Pick<AnamnesisSummary, 'id' | 'patient' | 'channel' | 'draft'>

interface Failure {
  title: string
  description: string
  actions?: ReactNode
}

interface FreshLink {
  url: string
  patientName: string
  expiresAt: string
}

export function useAnamnesisActions(options: { onCanceled?: () => void } = {}) {
  const resendMutation = useResendAnamnesis()
  const cancelMutation = useCancelAnamnesis()
  const [canceling, setCanceling] = useState<{ target: ActionTarget; reason?: string } | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [freshLink, setFreshLink] = useState<FreshLink | null>(null)

  /** Reemite o link; sem `channel`, vale o canal da emissão. */
  function resend(target: ActionTarget, channel?: AnamnesisChannel) {
    const name = titleCase(target.patient.name)
    const used = channel ?? target.channel
    setFailure(null)
    setFreshLink(null)
    resendMutation.mutate(
      { id: target.id, channel },
      {
        onSuccess: (result) => {
          const url = result.link.url
          if (used === 'link' && url) {
            // O sistema não envia nada no canal `link`: o endereço novo fica na tela, com o QR.
            setFreshLink({
              url,
              patientName: target.patient.name,
              expiresAt: result.link.expiresAt,
            })
            void copyText(url).then((copied) => {
              if (copied) toast(LINK_COPIED)
            })
            return
          }
          toast(result.message)
        },
        onError: (error) => {
          const down = providerDown(error)
          if (down?.url) {
            const url = down.url
            const other: AnamnesisChannel = used === 'email' ? 'whatsapp' : 'email'
            setFailure({
              title: `O link de ${name} foi renovado, mas a mensagem não saiu`,
              description: `${errorMessage(error)} O link novo já vale: copie e entregue você, ou envie por ${CHANNEL_IN_SENTENCE[other]}.`,
              actions: (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void copyText(url).then((copied) => {
                        if (copied) toast(LINK_COPIED)
                        else
                          setFreshLink({
                            url,
                            patientName: target.patient.name,
                            expiresAt: '',
                          })
                      })
                    }
                  >
                    Copiar link
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => resend(target, other)}>
                    Enviar por {CHANNEL_IN_SENTENCE[other]}
                  </Button>
                </>
              ),
            })
            return
          }
          setFailure({
            title: `Não foi possível reenviar a anamnese de ${name}`,
            description: fieldErrors(error).channel ?? errorMessage(error),
          })
        },
      },
    )
  }

  /** Abre a confirmação; `reason` é o motivo opcional, quando a tela o pediu antes. */
  function askCancel(target: ActionTarget, reason?: string) {
    setCanceling({ target, reason })
  }

  function confirmCancel() {
    if (!canceling) return
    const { target, reason } = canceling
    const name = titleCase(target.patient.name)
    setFailure(null)
    cancelMutation.mutate(
      { id: target.id, reason: reason?.trim() || undefined },
      {
        onSuccess: () => {
          setCanceling(null)
          toast(`Anamnese de ${name} cancelada`)
          options.onCanceled?.()
        },
        onError: (error) => {
          setCanceling(null)
          setFailure({
            title: `Não foi possível cancelar a anamnese de ${name}`,
            description: errorMessage(error),
          })
        },
      },
    )
  }

  const cancelTarget: CancelTarget | null = canceling
    ? {
        id: canceling.target.id,
        patientName: canceling.target.patient.name,
        draft: canceling.target.draft ?? null,
      }
    : null

  const notice =
    failure || freshLink ? (
      <div className="flex flex-col gap-4">
        {failure ? (
          <ActionError
            title={failure.title}
            description={failure.description}
            actions={failure.actions}
            onDismiss={() => setFailure(null)}
          />
        ) : null}
        {freshLink ? (
          <AttentionBlock
            variant="lilac"
            role="note"
            title={`Link novo de ${titleCase(freshLink.patientName)}`}
            aria-label="Link novo da anamnese"
          >
            <p>O link anterior deixou de valer. Entregue este à paciente: vale por 7 dias.</p>
            <LinkPanel
              className="mt-3"
              url={freshLink.url}
              patientName={freshLink.patientName}
              expiresAt={freshLink.expiresAt || undefined}
            />
            <Button variant="text" className="mt-4" onClick={() => setFreshLink(null)}>
              Fechar
            </Button>
          </AttentionBlock>
        ) : null}
      </div>
    ) : null

  const dialog = (
    <CancelAnamnesisDialog
      target={cancelTarget}
      loading={cancelMutation.isPending}
      onClose={() => setCanceling(null)}
      onConfirm={confirmCancel}
      onResend={() => {
        const current = canceling
        setCanceling(null)
        if (current) resend(current.target)
      }}
    />
  )

  return {
    resend,
    askCancel,
    /** O que falhou e o link novo, quando houver: fica acima da lista até a pessoa fechar. */
    notice,
    /** O modal de confirmação do cancelamento; monte uma vez na tela. */
    dialog,
    isResending: resendMutation.isPending,
    isCanceling: cancelMutation.isPending,
  }
}
